import type { EntitlementAction, EntitlementDecision } from './billing.js';
import { RENEWAL_RETRY_DAYS } from './billing-pay.js';
import type { SubscriptionStatus } from './enums.js';
import type { NotificationType } from './notifications.js';
import { OPERATOR_ACTIVE_SUBSCRIPTION_STATUSES } from './operator.js';
import type { PlanTier } from './plans.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ABONELİK UYARISI — kabuk şeridi, günlük katman, sohbet notu, hatırlatmalar
 * (1 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ürün sahibi: "Plan bitince adama panelde bildirim gelmiyor … topbar şeridi
 * gibi gelsin, bildirim gitsin, hatırlatma gitsin — adama yapışalım."
 * Deneme bitip `EXPIRED` olan alanda AI'ın yazma araçları SESSİZCE
 * kapanıyordu; `billing.trial_ended` bildirimi yalnız zile düşüyordu ve
 * kalıcı bir iz yoktu.
 *
 * Bu dosya DURUMUN TÜRETİMİ ve HATIRLATMA TAKVİMİ — ikisi de SAF:
 *
 *   · `deriveBillingAlert` — şeridin/katmanın/sohbet notunun okuduğu tek
 *     cevap. Sunucu (`GET /billing/alert`) çağırıyor; ekran kendi kuralını
 *     YAZMIYOR (`EntitlementService` başlığındaki gerekçe: iki ekran iki
 *     farklı yorum yazarsa biri "AI durdu" der, öteki demez).
 *   · `reminderFor` — worker'ın saatlik bakım turunun "bugün bu alana hangi
 *     hatırlatma gider" sorusu. Yerel saat penceresi ve gün takvimi burada.
 *
 * 🔴 "NE DURDU" UYDURULMUYOR. Şimdiki durum `EntitlementService`in GERÇEK
 * kararlarından (`stopped`), ileride duracaklar (`willStop`) ise o servisin
 * uyguladığı listelerden (`GRACE_BLOCKED_ACTIONS`, biten planda yalnız dışa
 * aktarma açık) türüyor. API'deki `billing-alert.spec.ts` bu listelerin
 * servisle aynı kaldığını gerçek servisi çağırarak doğruluyor.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Neyin durduğu
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Durabilen işler — ekran bunları `billing.stopped.<anahtar>` ile çevirir.
 * `operator` = arka plan AI'ı (otonom operatör); yalnız alanda AÇIKSA listeye
 * girer — kapalı bir şeyin "durdu" denmesi yalan olurdu.
 */
export const BILLING_STOP_FEATURES = ['agent_write', 'automation', 'creative', 'operator'] as const;
export type BillingStopFeature = (typeof BILLING_STOP_FEATURES)[number];

/** İşi kapatan hak (`EntitlementService`) — `operator` hakla değil durumla kapanıyor. */
export const BILLING_FEATURE_ACTION: Readonly<Record<Exclude<BillingStopFeature, 'operator'>, EntitlementAction>> = {
  agent_write: 'agent.write',
  automation: 'automation.run',
  creative: 'creative.generate',
};

/**
 * Ek süre (yumuşak sınır) bitince reddedilen eylemler. TEK KAYNAK:
 * `EntitlementService` bu listeyi uyguluyor, şerit bundan "ek süre bitince
 * ne duracak" cümlesini kuruyor. Operatör ek sürede ÇALIŞMAYA DEVAM ediyor
 * (`OPERATOR_ACTIVE_SUBSCRIPTION_STATUSES` `GRACE`i içeriyor).
 */
export const GRACE_BLOCKED_ACTIONS: readonly EntitlementAction[] = [
  'automation.run',
  'creative.generate',
  'agent.write',
];

/** Kapanmış planda (EXPIRED/CANCELED) — dışa aktarma dışındaki HER hak reddediliyor. */
export const ENDED_PLAN_OPEN_ACTIONS: readonly EntitlementAction[] = ['export.run'];

/**
 * PLANIN bir eylemi kapatıp kapatmadığı — `EntitlementService`in plan dalı
 * (abonelik yok · bitti · ek süre bitti), SAF hâli. `null` = plan engel değil
 * (kota/kredi soruları ayrı, onlar servisin kendisinde).
 *
 * Neden shared: worker'ın ZAMANLANMIŞ kural yolu (`automation-run.service`)
 * API'deki servise ulaşamıyor (ayrı süreç) ve 1 Eki 2026'ya kadar plan
 * kapısını HİÇ sormuyordu — süresi dolmuş alanda zamanlanmış kurallar
 * çalışmaya ve bütçe yazmaya devam ediyordu, şerit ise "otomasyonlar
 * duraklatıldı" diyordu. İki süreç aynı fonksiyonu okuyor; ikinci bir yorum yok.
 */
export function planBlocks(
  subscription: { status: SubscriptionStatus; graceUntil: Date | null } | null,
  action: EntitlementAction,
  now: Date = new Date(),
): 'SUBSCRIPTION_MISSING' | 'SUBSCRIPTION_INACTIVE' | 'GRACE_EXPIRED' | null {
  if (!subscription) return 'SUBSCRIPTION_MISSING';
  // Okuma/veri çıkarma her koşulda açık (§7 "veri rehin alınmaz").
  if (ENDED_PLAN_OPEN_ACTIONS.includes(action)) return null;
  if (subscription.status === 'CANCELED' || subscription.status === 'EXPIRED') return 'SUBSCRIPTION_INACTIVE';
  const graceExpired =
    subscription.status === 'GRACE' &&
    subscription.graceUntil !== null &&
    subscription.graceUntil.getTime() <= now.getTime();
  if (graceExpired && GRACE_BLOCKED_ACTIONS.includes(action)) return 'GRACE_EXPIRED';
  return null;
}

/** Bir durum geçişinden sonra hangi işler duracak — ileriye dönük cümle için. */
export function featuresStoppedAfter(
  transition: 'ended' | 'grace_expired',
  operatorEnabled: boolean,
): BillingStopFeature[] {
  const out: BillingStopFeature[] = [];
  for (const [feature, action] of Object.entries(BILLING_FEATURE_ACTION) as [
    Exclude<BillingStopFeature, 'operator'>,
    EntitlementAction,
  ][]) {
    const denied =
      transition === 'ended' ? !ENDED_PLAN_OPEN_ACTIONS.includes(action) : GRACE_BLOCKED_ACTIONS.includes(action);
    if (denied) out.push(feature);
  }
  if (operatorEnabled && transition === 'ended') out.push('operator');
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Durum
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Öncelik sırasıyla (ilk eşleşen kazanır):
 *
 *   expired         plan süresi doldu (deneme ya da ödenmeyen yenileme)
 *   canceled        iptal edilen abonelik dönem sonunda kapandı
 *   grace_expired   harcama sınırı aşıldı, ek süre bitti → para harcatan işler durdu
 *   past_due        yenileme ödemesi alınamadı, yeniden deneniyor (erişim AÇIK)
 *   grace           harcama sınırı aşıldı, ek süre işliyor
 *   ending          deneme ya da iptal edilmiş dönem ≤3 gün içinde bitiyor
 *   credits         kredi bitti — kreatif üretimi durdu (hak kararından)
 */
export const BILLING_ALERT_KINDS = [
  'expired',
  'canceled',
  'grace_expired',
  'past_due',
  'grace',
  'ending',
  'credits',
] as const;
export type BillingAlertKind = (typeof BILLING_ALERT_KINDS)[number];

/** Bitişe kaç gün kala şerit "bitiyor" demeye başlıyor. */
export const BILLING_ENDING_WARN_DAYS = 3;

export interface BillingAlert {
  kind: BillingAlertKind;
  /**
   * `alert` — bir şey DURDU ya da ödeme gecikti; şerit KAPATILAMAZ, en fazla
   * tek satıra küçülür. `warn` — yaklaşan bir bitiş ya da kredi; günlük
   * kapatılabilir.
   */
  tone: 'alert' | 'warn';
  dismissible: boolean;
  /**
   * Kindin tarihi: bittiyse BİTİŞ anı, bitecekse SON GÜN. Kredi için dönem
   * sonu (kredi o gün yenilenir). Bilinmiyorsa `null`.
   */
  at: string | null;
  /// Şu an duran işler — `EntitlementService`in gerçek kararlarından.
  stopped: BillingStopFeature[];
  /// `at` geldiğinde duracak işler (yalnız henüz durmamışsa dolu).
  willStop: BillingStopFeature[];
  plan: PlanTier;
  /// Deneme planı mı — metin "deneme süren" / "planın" diye ayrılıyor.
  trial: boolean;
}

export interface BillingAlertInput {
  subscription: {
    plan: PlanTier;
    status: SubscriptionStatus;
    currentPeriodEnd: Date;
    paidThrough: Date | null;
    graceUntil: Date | null;
    nextRenewalAt: Date | null;
    renewalFailures: number;
    cancelAtPeriodEnd: boolean;
  } | null;
  /// `EntitlementService.canMany` — en az şeridin baktığı üç eylem.
  entitlements: Pick<Record<EntitlementAction, EntitlementDecision>, 'agent.write' | 'automation.run' | 'creative.generate'>;
  operatorEnabled: boolean;
}

const DAY_MS = 86_400_000;

/**
 * Gecikmiş yenilemenin abonelik SONA ERMEDEN önceki son anı.
 *
 * `PayService.onRenewalFailed`in takvimi: ilk ret ödenmiş dönem sonunda,
 * sonra `RENEWAL_RETRY_DAYS` (1, 3, 5) gün arayla yeniden deneme; deneme
 * sayısı listeyi aşınca `EXPIRED`. Son deneme vadelenmişse (`renewalFailures`
 * listenin boyuna ulaştı) bitiş o andır; değilse dönem sonu + toplam gün.
 * Geçici sağlayıcı arızası birkaç saat kaydırabilir — tarih "en geç" değil
 * "planlanan" bitiş.
 */
export function pastDueDeadline(subscription: {
  paidThrough: Date | null;
  nextRenewalAt: Date | null;
  renewalFailures: number;
}): Date | null {
  if (subscription.renewalFailures >= RENEWAL_RETRY_DAYS.length && subscription.nextRenewalAt) {
    return subscription.nextRenewalAt;
  }
  if (!subscription.paidThrough) return subscription.nextRenewalAt;
  const total = RENEWAL_RETRY_DAYS.reduce((sum, days) => sum + days, 0);
  return new Date(subscription.paidThrough.getTime() + total * DAY_MS);
}

/**
 * Kapanmış planın BİTİŞ anı. Satırda ayrı bir "bittiği an" kolonu yok:
 * deneme `currentPeriodEnd`te, iptal edilen dönem `paidThrough`ta biter;
 * ödenemeyen yenileme ise yeniden denemeler tükenince (`pastDueDeadline`).
 */
export function planEndedAt(subscription: {
  plan: PlanTier;
  currentPeriodEnd: Date;
  paidThrough: Date | null;
  nextRenewalAt: Date | null;
  renewalFailures: number;
}): Date {
  if (subscription.renewalFailures > RENEWAL_RETRY_DAYS.length && subscription.paidThrough) {
    return pastDueDeadline({ ...subscription, nextRenewalAt: null }) ?? subscription.paidThrough;
  }
  return subscription.paidThrough ?? subscription.currentPeriodEnd;
}

/**
 * Şeridin durumu — `null` = gösterilecek bir şey yok.
 *
 * Satır yoksa (`SUBSCRIPTION_MISSING`) da `null`: plansız alan kabuğa hiç
 * giremiyor, kurulum sihirbazına yönleniyor (`(shell)/layout.tsx`).
 */
export function deriveBillingAlert(input: BillingAlertInput, now: Date = new Date()): BillingAlert | null {
  const sub = input.subscription;
  if (!sub) return null;

  const stopped: BillingStopFeature[] = [];
  for (const [feature, action] of Object.entries(BILLING_FEATURE_ACTION) as [
    Exclude<BillingStopFeature, 'operator'>,
    'agent.write' | 'automation.run' | 'creative.generate',
  ][]) {
    if (!input.entitlements[action].allowed) stopped.push(feature);
  }
  if (input.operatorEnabled && !OPERATOR_ACTIVE_SUBSCRIPTION_STATUSES.includes(sub.status)) {
    stopped.push('operator');
  }

  const trial = sub.plan === 'TRIAL';
  const base = { plan: sub.plan, trial, stopped };
  const iso = (date: Date | null) => (date ? date.toISOString() : null);

  if (sub.status === 'EXPIRED' || sub.status === 'CANCELED') {
    return {
      ...base,
      kind: sub.status === 'EXPIRED' ? 'expired' : 'canceled',
      tone: 'alert',
      dismissible: false,
      at: iso(planEndedAt(sub)),
      willStop: [],
    };
  }

  if (sub.status === 'GRACE' && sub.graceUntil && sub.graceUntil.getTime() <= now.getTime()) {
    return { ...base, kind: 'grace_expired', tone: 'alert', dismissible: false, at: iso(sub.graceUntil), willStop: [] };
  }

  if (sub.status === 'PAST_DUE') {
    return {
      ...base,
      kind: 'past_due',
      tone: 'alert',
      dismissible: false,
      at: iso(pastDueDeadline(sub)),
      willStop: featuresStoppedAfter('ended', input.operatorEnabled).filter((f) => !stopped.includes(f)),
    };
  }

  if (sub.status === 'GRACE' && sub.graceUntil) {
    return {
      ...base,
      kind: 'grace',
      tone: 'warn',
      dismissible: true,
      at: iso(sub.graceUntil),
      willStop: featuresStoppedAfter('grace_expired', input.operatorEnabled).filter((f) => !stopped.includes(f)),
    };
  }

  /* Biten dönem: deneme ya da dönem sonunda iptal. Kalan süre bitişe ≤3 gün
     ve bitiş henüz gelmedi (gelmişse worker birazdan durumu çeviriyor — o
     arada "bitiyor" demek doğru). */
  const endsAt =
    sub.status === 'TRIALING'
      ? sub.currentPeriodEnd
      : sub.cancelAtPeriodEnd && (sub.status === 'ACTIVE' || sub.status === 'GRACE')
        ? (sub.paidThrough ?? sub.currentPeriodEnd)
        : null;
  if (endsAt && endsAt.getTime() - now.getTime() <= BILLING_ENDING_WARN_DAYS * DAY_MS) {
    return {
      ...base,
      kind: 'ending',
      tone: 'warn',
      dismissible: true,
      at: iso(endsAt),
      willStop: featuresStoppedAfter('ended', input.operatorEnabled).filter((f) => !stopped.includes(f)),
    };
  }

  /* Kredi: kararın KENDİSİ (`INSUFFICIENT_CREDITS`) — bakiye sayısına ayrıca
     bakılmıyor (rezerve düşümü, sınırsız plan… hepsini servis biliyor). */
  const creative = input.entitlements['creative.generate'];
  if (!creative.allowed && creative.reasonCode === 'INSUFFICIENT_CREDITS') {
    return { ...base, kind: 'credits', tone: 'warn', dismissible: true, at: creative.until, willStop: [] };
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Çağrı — rol bazlı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Şeridin/katmanın düğmesi.
 *
 * Ödemeyi yalnız `billing.manage` (OWNER) yapabiliyor. Diğer rollere ödeme
 * sayfasının bağlantısı verilmez (girdikleri anda 403 görürlerdi) — onlara
 * "Sahibinden iste": sahibe tek tıkla hatırlatma bildirimi, alan başına
 * günde bir (`POST /billing/alert/ask-owner`).
 */
export type BillingAlertAction =
  | { kind: 'link'; href: string; label: 'start' | 'pay' | 'upgrade' | 'choose' | 'credits' }
  | { kind: 'ask_owner' };

export function billingAlertAction(alert: Pick<BillingAlert, 'kind' | 'trial'>, canManage: boolean): BillingAlertAction {
  if (!canManage) return { kind: 'ask_owner' };
  switch (alert.kind) {
    case 'expired':
    case 'canceled':
      return { kind: 'link', href: '/dashboard/settings/billing?plans=open', label: 'start' };
    case 'past_due':
      return { kind: 'link', href: '/dashboard/settings/billing?pay=renewal', label: 'pay' };
    case 'grace':
    case 'grace_expired':
      return { kind: 'link', href: '/dashboard/settings/billing?plans=open', label: 'upgrade' };
    case 'ending':
      return { kind: 'link', href: '/dashboard/settings/billing?plans=open', label: 'choose' };
    case 'credits':
      return { kind: 'link', href: '/dashboard/settings/billing/credits', label: 'credits' };
  }
}

/** Günlük katman (hatırlatma) hangi durumlarda — yalnız plan BİTMİŞKEN ve sahibe. */
export function billingAlertNags(alert: Pick<BillingAlert, 'kind'> | null, canManage: boolean): boolean {
  return canManage && alert !== null && (alert.kind === 'expired' || alert.kind === 'canceled');
}

/** `GET /billing/alert` — her rol okur (MEMBER/VIEWER da şeridi görmeli). */
export interface BillingAlertResponse {
  alert: BillingAlert | null;
  /// `billing.manage` — düğme "Aboneliği başlat" mı "Sahibinden iste" mi.
  canManage: boolean;
  /// Bugün (alanın yerel günü) sahibe zaten haber verildi mi — düğme kapanır.
  ownerAskedToday: boolean;
  /// Alanın yerel takvimindeki bugün (`YYYY-MM-DD`) — günlük kapatma ve
  /// günlük katman bu anahtarla hatırlanıyor (tarayıcının günüyle değil).
  today: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Yerel takvim
// ═══════════════════════════════════════════════════════════════════════════

/** Alanın saat dilimindeki gün (`YYYY-MM-DD`). Geçersiz IANA adı → UTC. */
export function localDayKey(date: Date, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/** Alanın saat dilimindeki saat (0–23). Geçersiz IANA adı → UTC. */
export function localHour(date: Date, timezone: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const hour = Number(parts.find((part) => part.type === 'hour')?.value);
    return Number.isFinite(hour) ? hour % 24 : date.getUTCHours();
  } catch {
    return date.getUTCHours();
  }
}

/** İki `YYYY-MM-DD` arasındaki takvim günü farkı (`to − from`). */
export function dayKeyDiff(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

// ═══════════════════════════════════════════════════════════════════════════
// Hatırlatma takvimi (worker, saatlik bakım turu)
// ═══════════════════════════════════════════════════════════════════════════

/** Bitişten ÖNCE: T-3, T-1, T-0 (yerel takvim günü). */
export const REMINDER_LEAD_DAYS = [3, 1, 0] as const;
/**
 * Bitişten SONRA: +1, +3, +7, +14, +30, sonra ayda bir — en fazla üç ay.
 * Ücretli plan bittiği GÜN de (0) bir kez: denemenin bitişi kendi bildirimini
 * (`billing.trial_ended`) bitiş anında alıyor, ödenmeyen yenileme ve iptal
 * almıyordu — plan sessizce kapanıyordu.
 */
export const REMINDER_FOLLOW_UP_DAYS = [1, 3, 7, 14, 30, 60, 90] as const;
/// Yerel saat penceresi [başlangıç, bitiş) — gece yarısı posta/SMS yok.
export const REMINDER_LOCAL_WINDOW = { fromHour: 9, toHour: 20 } as const;

/**
 * Hangi zincir:
 *   trial     deneme bitiyor           → `billing.trial_ending`   (T-3/T-1/T-0)
 *   past_due  ödeme alınamıyor          → `billing.payment_overdue` (T-3/T-1/T-0, bitiş = yeniden denemeler tükenir)
 *   grace     ek süre bitiyor           → `billing.grace_ending`   (T-3/T-1/T-0)
 *   ended     plan bitti (EXPIRED/CANCELED) → `billing.subscription_reminder` (+1 … +90)
 *   grace_expired ek süre bitti         → `billing.grace_ended`    (+1 … +90)
 */
export type ReminderTrack = 'trial' | 'past_due' | 'grace' | 'ended' | 'grace_expired';

export type ReminderDecision =
  | {
      due: true;
      track: ReminderTrack;
      type: NotificationType;
      /// Bitişe göre gün: negatif = önce (T-3 → -3), pozitif = sonra.
      offset: number;
      /// Alanın yerel bugünü — "alan + gün başına bir" anahtarı.
      day: string;
      params: Record<string, string>;
    }
  | {
      due: false;
      reason: 'active' | 'outside_window' | 'not_a_reminder_day' | 'too_old' | 'no_deadline';
    };

export interface ReminderInput {
  plan: PlanTier;
  status: SubscriptionStatus;
  currentPeriodEnd: Date;
  paidThrough: Date | null;
  graceUntil: Date | null;
  nextRenewalAt: Date | null;
  renewalFailures: number;
  cancelAtPeriodEnd: boolean;
  timezone: string;
}

/**
 * Bugün bu alana hangi hatırlatma gidiyor — SAF.
 *
 * İdempotans çağıranın: aynı yerel gün için en fazla BİR hatırlatma (worker
 * `SET NX` işareti). Takvim gün bazlı olduğu için aynı aşama iki farklı güne
 * düşemez; saatlik tur penceredeki ilk saatte gönderir, sonrakiler işarete
 * çarpar.
 *
 * Kaçırılan gün TELAFİ EDİLMİYOR (worker o gün kapalıydıysa +3, 4. gün
 * gönderilmez): "3 gün önce bitti" diyen bir metin 4. gün yanlış olurdu ve
 * bir sonraki aşama zaten geliyor.
 */
export function reminderFor(input: ReminderInput, now: Date = new Date()): ReminderDecision {
  const tz = input.timezone;
  const hour = localHour(now, tz);
  const today = localDayKey(now, tz);

  let track: ReminderTrack;
  let deadline: Date | null;
  let type: NotificationType;
  let lead: boolean;

  if (input.status === 'TRIALING') {
    track = 'trial';
    deadline = input.currentPeriodEnd;
    type = 'billing.trial_ending';
    lead = true;
  } else if (input.status === 'PAST_DUE') {
    track = 'past_due';
    deadline = pastDueDeadline(input);
    type = 'billing.payment_overdue';
    lead = true;
  } else if (input.status === 'GRACE' && input.graceUntil && input.graceUntil.getTime() > now.getTime()) {
    track = 'grace';
    deadline = input.graceUntil;
    type = 'billing.grace_ending';
    lead = true;
  } else if (input.status === 'GRACE' && input.graceUntil) {
    track = 'grace_expired';
    deadline = input.graceUntil;
    type = 'billing.grace_ended';
    lead = false;
  } else if (input.status === 'EXPIRED' || input.status === 'CANCELED') {
    track = 'ended';
    deadline = planEndedAt(input);
    type = 'billing.subscription_reminder';
    lead = false;
  } else {
    return { due: false, reason: 'active' };
  }

  if (!deadline) return { due: false, reason: 'no_deadline' };
  const deadlineDay = localDayKey(deadline, tz);
  const offset = dayKeyDiff(deadlineDay, today);

  if (lead) {
    /* Önce zinciri: bitiş henüz gelmedi. (Gelmişse durum birazdan değişiyor —
       "bugün bitiyor" demek için geç.) */
    if (deadline.getTime() <= now.getTime()) return { due: false, reason: 'not_a_reminder_day' };
    if (!(REMINDER_LEAD_DAYS as readonly number[]).includes(-offset)) {
      return { due: false, reason: 'not_a_reminder_day' };
    }
  } else {
    if (offset > REMINDER_FOLLOW_UP_DAYS[REMINDER_FOLLOW_UP_DAYS.length - 1]!) return { due: false, reason: 'too_old' };
    const followDays: readonly number[] =
      track === 'ended' && input.plan !== 'TRIAL' ? [0, ...REMINDER_FOLLOW_UP_DAYS] : REMINDER_FOLLOW_UP_DAYS;
    if (!followDays.includes(offset)) return { due: false, reason: 'not_a_reminder_day' };
  }

  /* Pencere EN SON: gün doğru ama saat değilse "bugün" yine gelir — tur bir
     saat sonra tekrar soruyor. */
  if (hour < REMINDER_LOCAL_WINDOW.fromHour || hour >= REMINDER_LOCAL_WINDOW.toHour) {
    return { due: false, reason: 'outside_window' };
  }

  /* Tarih metne YEREL GÜN olarak (`YYYY-MM-DD`) giriyor: alıcının dili
     bilinmiyor (e-posta, SMS, zil ayrı ayrı çevriliyor) ve bu biçim on üç
     dilin hepsinde aynı okunuyor. Saat yazmak alıcının saat dilimini
     varsaymak olurdu. */
  const params: Record<string, string> =
    track === 'ended' ? { date: deadlineDay } : track === 'grace_expired' ? {} : { until: deadlineDay };

  return { due: true, track, type, offset, day: today, params };
}
